import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ReactComponent as TranslateIcon } from "../../../../assets/translate.svg";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  Divider,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { usePortal } from "../../../../context/PortalsContext";
import { getS3PresignedUrl } from "../../../../services/api/apiClient";

import { GroupResult } from "../../../../services/api/types/portalGroup/portalGroup";
import {
  GetS3PresignedUrlResult,
  PresignFileType,
} from "../../../../services/api/types/portalConfiguration/uploadPhoto";
import Toast from "../../../../utils/Toast";
import { MAX_LENGTH_SHORT_LABEL } from "../../../../utils/inputLimits";
import { UploadFile } from "../../../Assignments/AssignmentUploads/components/UploadFile";
import { TranslateFieldModal } from "../../../Assignments/AssignmentUploads/components/TranslateFieldModal";
import { brand } from "../../../../theme/themePrimitives";

interface editGroupModalProps {
  name: string;
  nameError: string;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  nameTranslations: Record<string, string>;
  setNameTranslations: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  openEditGroupModal: boolean;
  setOpenEditGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  buttonLoading: boolean;
  addNewGroup: () => void;
  selectedCourseModal: GroupResult;
  setThumbnailURL: React.Dispatch<React.SetStateAction<string | null>>;
}

export const EditNewGroupModal: React.FC<editGroupModalProps> = ({
  name,
  nameError,
  handleNameChange,
  nameTranslations,
  setNameTranslations,
  openEditGroupModal,
  setOpenEditGroupModal,
  modalTitle,
  buttonLoading,
  addNewGroup,
  setThumbnailURL,
  selectedCourseModal,
}) => {
  const [nameTranslateModalOpen, setNameTranslateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const { portals } = usePortal();
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  const handleImageUpload = async (resizedFile: File) => {
    try {
      setLoading(true);
      const response: GetS3PresignedUrlResult = await getS3PresignedUrl(
        portals.selectedPortal?.portalUid!,
        {
          fileName: resizedFile.name,
          contentType: resizedFile.type,
          fileType: PresignFileType.GROUP_THUMBNAIL,
        },
      );
      const { uploadUrl, path } = response;
      setThumbnailURL(path);
      await axios.put(uploadUrl, resizedFile, {
        headers: {
          "Content-Type": resizedFile.type,
        },
      });
    } catch (error: any) {
      console.log("Error:", error);
      setErrorMessage(error.response.data);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailChange = (file: File | null) => {
    if (!file) return;
    setThumbnailFile(file);
    handleImageUpload(file);
  };

  useEffect(() => {
    if (!openEditGroupModal) {
      setThumbnailFile(null);
    }
  }, [openEditGroupModal]);

  return (
    <>
      <Modal
        open={openEditGroupModal}
        onClose={() => setOpenEditGroupModal(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
            padding: "16px",
            width: "50%",
          }}
        >
          {errorMessage && (
            <Toast
              open={Boolean(errorMessage)}
              message={errorMessage}
              duration={2000}
              onClose={() => setErrorMessage("")}
              type={"error"}
            />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #D0D5DD",
              pb: 2,
              mb: 1,
            }}
          >
            <Typography variant="h5">{modalTitle}</Typography>
            <IconButton
              sx={{
                border: "none",
              }}
              onClick={() => setOpenEditGroupModal(false)}
            >
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>
          <Stack>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2">
                  Name
                  <Box component="span" sx={{ color: `var(--error-700)` }}>
                    *
                  </Box>
                </Typography>
                <TranslateIcon
                  onClick={() =>
                    name.trim() !== "" && setNameTranslateModalOpen(true)
                  }
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "2px",
                    borderRight:
                      name.trim() !== ""
                        ? `1px solid ${brand[600]}`
                        : "1px solid #98A2B3",
                    borderBottom:
                      name.trim() !== ""
                        ? `1px solid ${brand[600]}`
                        : "1px solid #98A2B3",
                    cursor: name.trim() !== "" ? "pointer" : "default",
                    color: name.trim() !== "" ? brand[600] : "#98A2B3",
                  }}
                />
              </Box>
              <TextField
                fullWidth
                id="name"
                placeholder="Enter group name"
                type="text"
                value={name}
                onChange={handleNameChange}
                variant="outlined"
                size="small"
                error={Boolean(nameError)}
                helperText={nameError}
                slotProps={{
                  input: {
                    endAdornment: nameError && (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ),
                  },
                  htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
                }}
              />
              {loading ? (
                <Box
                  sx={{
                    minHeight: "200px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <UploadFile
                  value={thumbnailFile}
                  onChange={handleThumbnailChange}
                  mode="thumbnail"
                  disabled={loading}
                  label="Thumbnail"
                  labelVariant="body2"
                  url={selectedCourseModal?.thumbnail ?? undefined}
                />
              )}
              <Divider sx={{ my: "15px !important" }} />
              <Stack>
                {buttonLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Button
                    onClick={addNewGroup}
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: `${brand[600]} !important`,
                      color: `white`,
                      "&:hover": {
                        bgcolor: `${brand[700]} !important`,
                        fontWeight: 700,
                      },
                    }}
                  >
                    <Typography>Save Changes</Typography>
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      <TranslateFieldModal
        open={nameTranslateModalOpen}
        onClose={() => setNameTranslateModalOpen(false)}
        fieldLabel="Group name"
        englishValue={name}
        onEnglishValueChange={(value) =>
          handleNameChange({
            target: { value },
          } as React.ChangeEvent<HTMLInputElement>)
        }
        translations={nameTranslations}
        onSave={setNameTranslations}
      />
    </>
  );
};
